/**
 * Council Trail v0.1 — Hill Climber Lab (dev surface).
 *
 * One page to exercise the propose → review → approve loop without
 * spinning up a real peer LLM:
 *
 *   1. "Spin up test crew" — uses the existing useConveneCrew flow with
 *      the first 3 personas the user has access to (or as many as exist).
 *      Lands on the new trail; ?session= query carries the new id back
 *      to the lab so the inject chips know which session to drive.
 *
 *   2. "Inject 1 / Inject 5" — POST to council-lab inject endpoints.
 *      Each injection fires PROPOSAL_QUEUED, the ProposalsChip in
 *      TrailheadHeader updates live, and the operator can open the
 *      review dialog from there to approve/dismiss.
 *
 *   3. "Wipe pending" — hard-deletes pending rows so the chip count
 *      resets. Resolved rows stay (audit trail preserved).
 *
 *   4. Live status block — uses useCouncilProposals on the configured
 *      session id, so the operator can watch the queue change without
 *      navigating away from the lab.
 *
 * Desktop-first; dark-mode parity from day one (memory:
 * feedback_dark_mode_parity_first_class).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { CouncilProvider } from '../contexts/CouncilContext';
import { LensProvider } from '../contexts/LensContext';
import { useConveneCrew } from '../hooks/useConveneCrew';
import { useCouncilProposals } from '../hooks/useCouncilProposals';
import { usePersonas } from '../hooks/usePersonas';
import { councilLabApiClient } from '../services/councilLabApiClient';

const LAB_SESSION_KEY = 'council-lab.last-session';

export const CouncilLab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const querySessionId = searchParams.get('session');
  const [sessionInput, setSessionInput] = useState<string>(() => {
    if (querySessionId) return querySessionId;
    try {
      return localStorage.getItem(LAB_SESSION_KEY) ?? '';
    } catch {
      return '';
    }
  });

  // Persist whatever session the operator picks so reloads keep the
  // wiring point. Also reflect into the URL so links to the lab
  // pre-target a session (handy for sharing).
  useEffect(() => {
    try {
      if (sessionInput) localStorage.setItem(LAB_SESSION_KEY, sessionInput);
    } catch {
      // localStorage unavailable — tolerate.
    }
    const params = new URLSearchParams(searchParams);
    if (sessionInput) params.set('session', sessionInput);
    else params.delete('session');
    setSearchParams(params, { replace: true });
    // searchParams is omitted from deps intentionally — we read it via
    // closure and write a new instance per change, avoiding an effect loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionInput]);

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 text-gray-900 dark:text-gray-100">
      <Hero />
      <ConveneSection />
      <div className="mt-8 grid gap-6 md:grid-cols-[1fr_300px]">
        <DriveSection sessionId={sessionInput} setSessionId={setSessionInput} />
        {sessionInput ? (
          <CouncilProvider sessionId={sessionInput}>
            <LensProvider sessionId={sessionInput}>
              <StatusBlock sessionId={sessionInput} />
            </LensProvider>
          </CouncilProvider>
        ) : (
          <div
            data-testid="lab-status-empty"
            className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-4 text-sm text-gray-500 dark:text-gray-400"
          >
            Convene a crew or paste a session id to see live status.
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────

const Hero: React.FC = () => (
  <div
    data-testid="lab-hero"
    className="rounded-xl border border-violet-300 dark:border-violet-700 bg-gradient-to-br from-violet-50 to-amber-50 dark:from-violet-950/40 dark:to-amber-950/30 p-6"
  >
    <div className="flex items-baseline gap-3">
      <h1 className="text-2xl font-bold text-violet-900 dark:text-violet-100">
        🥾 Hill Climber Lab
      </h1>
      <span className="text-xs uppercase tracking-widest text-violet-600 dark:text-violet-300">
        v0.1 dev surface
      </span>
    </div>
    <p className="mt-2 text-sm text-violet-800 dark:text-violet-200">
      Exercise the <code className="font-mono">propose_fork → review → approve</code>{' '}
      loop without spinning up a real peer LLM. Inject synthetic proposals,
      open the trail, watch the chip count tick, click Approve, see the
      DAG paint.
    </p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────

const ConveneSection: React.FC = () => {
  const { data: personas } = usePersonas();
  const { convene, isConvening } = useConveneCrew();
  const candidates = useMemo(() => (personas || []).slice(0, 6), [personas]);
  const [n, setN] = useState<number>(3);

  const onConvene = useCallback(() => {
    const ids = candidates.slice(0, n).map((p: any) => p.id);
    if (ids.length < 2) return;
    void convene(ids);
  }, [candidates, n, convene]);

  return (
    <section
      data-testid="lab-convene-section"
      className="mt-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 mb-3">
        ① Convene a test crew
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        Uses the first {n} personas your account has access to. Navigates
        to the new trail; comes back to the lab via{' '}
        <code className="font-mono">?session=…</code> when you click the
        link at top of the new trail's URL bar.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {[2, 3, 4, 5].map((k) => (
          <button
            key={k}
            type="button"
            data-testid={`lab-convene-size-${k}`}
            onClick={() => setN(k)}
            disabled={k > candidates.length}
            className={`text-xs px-2.5 py-1 rounded border transition-colors
                       ${
                         n === k
                           ? 'bg-violet-500 text-white border-violet-500'
                           : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                       }
                       ${k > candidates.length ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            {k}-persona
          </button>
        ))}
        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
          ({candidates.length} candidates loaded)
        </span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          data-testid="lab-convene-go"
          onClick={onConvene}
          disabled={isConvening || candidates.length < 2}
          className={`text-sm px-4 py-1.5 rounded font-medium
                     bg-amber-500 hover:bg-amber-600
                     dark:bg-amber-600 dark:hover:bg-amber-500
                     text-white
                     ${isConvening || candidates.length < 2 ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {isConvening ? 'Convening…' : `Convene ${Math.min(n, candidates.length)}-persona crew`}
        </button>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Lands on the new trail — peer chip and dialog wired in TrailheadHeader.
        </span>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────

const DriveSection: React.FC<{
  sessionId: string;
  setSessionId: (next: string) => void;
}> = ({ sessionId, setSessionId }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<string>('');

  const wrap = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setLastResult('');
    try {
      const out = await fn();
      setLastResult(`${label}: ok · ${JSON.stringify(out)}`);
    } catch (e) {
      setLastResult(`${label}: ${(e as Error).message ?? 'error'}`);
    } finally {
      setBusy(null);
    }
  };

  const disabled = !sessionId || busy !== null;

  return (
    <section
      data-testid="lab-drive-section"
      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 mb-3">
        ② Drive proposals
      </h2>
      <label
        className="text-xs text-gray-600 dark:text-gray-400 block mb-1"
        htmlFor="lab-session-input"
      >
        Session id
      </label>
      <input
        id="lab-session-input"
        type="text"
        data-testid="lab-session-input"
        value={sessionId}
        onChange={(e) => setSessionId(e.target.value)}
        placeholder="paste a session id (from a /trailhead/… URL)"
        className="w-full text-sm font-mono px-2 py-1.5 mb-3 rounded
                   border border-gray-300 dark:border-gray-600
                   bg-white dark:bg-gray-800
                   text-gray-900 dark:text-gray-100
                   placeholder-gray-400 dark:placeholder-gray-500
                   focus:outline-none focus:ring-1 focus:ring-violet-500"
      />
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          data-testid="lab-inject-1"
          disabled={disabled}
          onClick={() =>
            wrap('inject 1', () => councilLabApiClient.injectProposal(sessionId))
          }
          className={`text-xs px-2.5 py-1 rounded border
                     bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200
                     border-violet-300 dark:border-violet-700
                     hover:bg-violet-200 dark:hover:bg-violet-900/60
                     ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          Inject 1
        </button>
        <button
          type="button"
          data-testid="lab-inject-5"
          disabled={disabled}
          onClick={() =>
            wrap('inject 5', () => councilLabApiClient.injectProposals(sessionId, 5))
          }
          className={`text-xs px-2.5 py-1 rounded border
                     bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200
                     border-violet-300 dark:border-violet-700
                     hover:bg-violet-200 dark:hover:bg-violet-900/60
                     ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          Inject 5
        </button>
        <button
          type="button"
          data-testid="lab-inject-10"
          disabled={disabled}
          onClick={() =>
            wrap('inject 10', () => councilLabApiClient.injectProposals(sessionId, 10))
          }
          className={`text-xs px-2.5 py-1 rounded border
                     bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200
                     border-violet-300 dark:border-violet-700
                     hover:bg-violet-200 dark:hover:bg-violet-900/60
                     ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          Inject 10
        </button>
        <button
          type="button"
          data-testid="lab-wipe"
          disabled={disabled}
          onClick={() =>
            wrap('wipe pending', () => councilLabApiClient.wipeProposals(sessionId))
          }
          className={`text-xs px-2.5 py-1 rounded border
                     bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200
                     border-red-300 dark:border-red-700
                     hover:bg-red-200 dark:hover:bg-red-900/60
                     ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          Wipe pending
        </button>
        {sessionId && (
          <Link
            to={`/trailhead/persona/unknown/${sessionId}`}
            data-testid="lab-open-trail"
            className="text-xs px-2.5 py-1 rounded border
                       bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100
                       border-amber-300 dark:border-amber-700
                       hover:bg-amber-200 dark:hover:bg-amber-900/60"
          >
            Open trail →
          </Link>
        )}
      </div>
      <pre
        data-testid="lab-last-result"
        className="text-[11px] font-mono rounded bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2 max-h-32 overflow-y-auto whitespace-pre-wrap text-gray-700 dark:text-gray-300"
      >
        {busy ? `${busy}…` : (lastResult || '(no requests yet)')}
      </pre>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────

const StatusBlock: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const { proposals, loading, error, refresh } = useCouncilProposals(sessionId);
  return (
    <aside
      data-testid="lab-status-block"
      className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/40 dark:bg-violet-950/30 p-4 self-start"
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs uppercase tracking-wide font-semibold text-violet-700 dark:text-violet-200">
          Live status
        </h2>
        <button
          type="button"
          data-testid="lab-status-refresh"
          onClick={() => void refresh()}
          className="text-[11px] px-2 py-0.5 rounded
                     bg-white/60 dark:bg-violet-900/40
                     text-violet-700 dark:text-violet-200
                     hover:bg-white dark:hover:bg-violet-900/60"
        >
          ⟳
        </button>
      </div>
      <p className="text-sm text-violet-900 dark:text-violet-100">
        <span className="tabular-nums text-2xl font-semibold">{proposals.length}</span>{' '}
        pending
      </p>
      {loading && (
        <p className="text-[11px] text-violet-600 dark:text-violet-300 mt-1">loading…</p>
      )}
      {error && (
        <p
          data-testid="lab-status-error"
          className="text-[11px] text-red-600 dark:text-red-400 mt-1"
        >
          {error.message ?? 'error'}
        </p>
      )}
      <p className="text-[11px] text-violet-700 dark:text-violet-300 mt-2 leading-snug">
        Refreshes on PROPOSAL_QUEUED + PROPOSAL_RESOLVED WS events
        (same hook as TrailheadHeader's chip).
      </p>
    </aside>
  );
};

export default CouncilLab;
